import { forwardRef, lazy, memo, Suspense, AriaRole } from 'react';
import { Sized } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import mapLegacyValues from './mapLegacyValues';
import { IconWrapper, StyledIcon } from './style';
import { IconVariant } from './types';

export interface IconProps extends Sized {
    value: IconName;
    alt?: string;
    role?: AriaRole;
    className?: string;
    variant?: IconVariant;
    forwardedRef?: React.Ref<HTMLSpanElement>;
}

const IconComponent = memo(
    ({ alt, size = 'medium', value, variant = 'inherit', forwardedRef, role, ...other }: IconProps) => {
        const newValue = mapLegacyValues(value);
        const pixelSize = size === 'small' ? 16 : 24;

        // Determine appropriate role and aria attributes
        const isDecorative = !alt;
        const defaultRole = isDecorative ? 'presentation' : 'img';

        const SvgIcon = lazy(async () => {
            let icon;

            try {
                // eslint-disable-next-line no-unsanitized/method
                icon = await import(
                    /* webpackChunkName: "icons", webpackMode: "lazy-once", webpackPreload: true */ `@optimus-web/assets/src/icons/${
                        newValue + '_' + pixelSize
                    }.svg?inline`
                );
            } catch (e) {
                return { default: () => null };
            }

            return icon;
        });

        return (
            <IconWrapper
                ref={forwardedRef}
                data-test-icon={newValue}
                size={size}
                {...(isDecorative && { 'aria-hidden': true })}
                {...other}
            >
                <Suspense fallback={null}>
                    <StyledIcon
                        as={SvgIcon}
                        size={size}
                        variant={variant}
                        viewBox={`0 0 ${pixelSize} ${pixelSize}`}
                        {...(alt && { 'aria-label': alt })}
                        role={role ?? defaultRole}
                    />
                </Suspense>
            </IconWrapper>
        );
    }
);

export const Icon = forwardRef<HTMLSpanElement, IconProps>((props, ref) => (
    <IconComponent {...props} forwardedRef={ref} />
));
